import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { ClientAgentReference } from '@/agent/references/store/schemas'
import type { UserFilesRAGRequest } from '@/agent/chat_form/store/schemas'

export type InlineEditSelection = {
  text: string
  anchorStart: string
  anchorEnd: string
  /** ProseMirror position range — preserved across component remounts for insert-below */
  from: number
  to: number
}

export type InlineEditReplacement = {
  /** Full updated markdown content from the BE */
  content: string
  /** References returned alongside the content. Upserted into the agent references
   * store by a listener in agent/conversations — inline-edit doesn't dispatch into
   * agent state directly. */
  references?: ClientAgentReference[]
}

export type InlineEditSession = {
  selection: InlineEditSelection | null
  pendingReplacement: InlineEditReplacement | null
  isSubmitting: boolean
}

export type InlineEditState = {
  sessions: { [conversationId: string]: InlineEditSession }
}

const initialState: InlineEditState = {
  sessions: {},
}

const defaultSession: InlineEditSession = { selection: null, pendingReplacement: null, isSubmitting: false }

function getOrCreateSession(state: InlineEditState, conversationId: string) {
  if (!state.sessions[conversationId]) {
    state.sessions[conversationId] = { ...defaultSession }
  }
  return state.sessions[conversationId]
}

const inlineEditSlice = createSlice({
  name: 'inlineEditState',
  initialState,
  reducers: {
    // Listener middleware trigger
    submitInlineEdit(
      state,
      action: PayloadAction<{
        conversationId: string
        artifactVersionId: string
        editInstruction: string
        caselawSearchFilter?: unknown
        lrrSearchFilter?: unknown
        userFilesRag?: UserFilesRAGRequest | null
        filePaths?: string[]
      }>
    ) {
      getOrCreateSession(state, action.payload.conversationId)
    },

    setSubmitting(state, action: PayloadAction<{ conversationId: string; isSubmitting: boolean }>) {
      getOrCreateSession(state, action.payload.conversationId).isSubmitting = action.payload.isSubmitting
    },

    setPendingReplacement(state, action: PayloadAction<{ conversationId: string; replacement: InlineEditReplacement }>) {
      getOrCreateSession(state, action.payload.conversationId).pendingReplacement = action.payload.replacement
    },

    clearPendingReplacement(state, action: PayloadAction<{ conversationId: string }>) {
      const session = state.sessions[action.payload.conversationId]
      if (session) {
        session.pendingReplacement = null
      }
    },

    setSelection(state, action: PayloadAction<{ conversationId: string; selection: InlineEditSelection }>) {
      getOrCreateSession(state, action.payload.conversationId).selection = action.payload.selection
    },

    clearSelection(state, action: PayloadAction<{ conversationId: string }>) {
      const session = state.sessions[action.payload.conversationId]
      if (session) {
        session.selection = null
      }
    },

    // Dispatched to signal the listener to abort the in-flight request
    cancelInlineEdit(_state, _action: PayloadAction<{ conversationId: string }>) {},

    nullifyData: () => initialState,
  },
})

export const InlineEditActions = inlineEditSlice.actions
export default inlineEditSlice.reducer
